import React, { useEffect, useMemo, useState } from "react"

import moment from "moment-timezone"
import { useFormContext } from "react-hook-form"
import { useNavigate } from "react-router-dom"

import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"

import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { DatePickerElement } from "@front-shared/elements/datePicker.element"
import { InputElement } from "@front-shared/elements/input.element"
import { InputErrorElement } from "@front-shared/elements/inputError.element"
import { ToastrHelper } from "@front-shared/helpers/toastr.helper"

import { CoverageCheckerRunningState } from "../../../config/checker.config"
import { GENERAL_CONFIG } from "../../../config/general.config"
import { ROUTES_CONFIG } from "../../../config/routes.config"
import { TOOLTIPS_CONFIG } from "../../../config/tooltips.config"
import { LookupHelper } from "../../../helpers/lookup.helper"
import { ProduktlyHelper } from "../../../helpers/produktly.helper"
import { UtilHelper } from "../../../helpers/util.helper"
import { useCheckerDynamicForm } from "../../../hooks/checkerDynamicForm.hooks"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { checkerSelector } from "../../../store/selectors/checker.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { checkerSetRunningState } from "../../../store/slices/checker.slice"
import {
    checkerRunAgenticPhoneChecks,
    checkerRunDiscoverChecks,
    checkerRunGeneralChecks,
    checkerRunMedicaidChecks,
    checkerRunSmartScanChecks,
} from "../../../store/thunks/checker.thunks"
import type { IDynamicCheckerFormInputData } from "../../../types/data.types"
import type { IThunkActionWithPracticeData } from "../../../types/general.types"
import { CheckerCreditsIndicatorComponent } from "./checkerCreditsIndicator.component"
import { AgenticTaxIdRenderingNameInput } from "./inputs/agenticTaxIdRenderingName.input"
import { MedicaidStateMemberIdGenderInput } from "./inputs/medicaidStateMemberIdGender.input"
import { MemberIdInput } from "./inputs/memberId.input"
import { NetworkInput } from "./inputs/network.input"
import { OptionalFieldsInput } from "./inputs/optionalFields.input"
import { PayerInput } from "./inputs/payer.input"
import { SessionRateInput } from "./inputs/sessionRate.input"

import undoPurpleIcon from "@front-shared/assets/images/icons/undo-purple.svg"

export const CheckerDynamicFormComponent: React.FunctionComponent<
    ICheckerDynamicFormComponentProps
> = props => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()

    const formMethods = useFormContext<IDynamicCheckerFormInputData>()

    const {
        handleSubmit,
        formState: { errors },
        control,
        setValue,
        watch,
        register,
        unregister,
    } = formMethods

    const dataWatcher = watch()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const { manualCheckType } = useAppSelector(checkerSelector)

    const effectiveManualCheckType = props.forcedCheckType || manualCheckType

    const [maxDob] = useState<Date>(new Date())

    // Track whether the payer field has been seeded (the form has held a payer
    // at least once). Gating on this rather than a first-render flag keeps it
    // correct even if the prefilled default arrives asynchronously / the input
    // mounts on a later render.
    const [isPayerSeeded, setIsPayerSeeded] = useState<boolean>(false)

    useEffect(() => {
        if (dataWatcher.payer) {
            setIsPayerSeeded(true)
        }
    }, [dataWatcher.payer])

    const isAgenticCheck = useMemo<boolean>(() => {
        return (
            !!manualCheckType &&
            [
                QuotaUsageType.AGENTIC_PORTAL,
                QuotaUsageType.AGENTIC_PHONE,
            ].includes(manualCheckType)
        )
    }, [manualCheckType])

    const {
        applyPredictedPayer,
        formDefaultValues: formDefaultValuesFromHook,
        formDefaultValuesContinuousMonitoring,
        formDefaultValuesMedicaid,
        initialDataWasSet,
        isMedicaidPayerSelected,
        memberIdInputKey,
        onPayerSelected,
        payerInputKey,
        payerSupportsDiscover,
        secondaryDataWasSet,
    } = useCheckerDynamicForm(formMethods, isAgenticCheck)

    const formDefaultValues =
        props.prefilledFormData || formDefaultValuesFromHook

    const coverageCheckId = useMemo<string>(() => {
        return formDefaultValues?.id || UtilHelper.generateUniqueVarchar(100)
    }, [formDefaultValues?.id])

    // Editable forms follow the live payer after the first seed, so a user
    // clear stays cleared. A read-only demo form always uses the policy payer:
    // the live value is undefined after the Smart Scan reset.
    const defaultPayer =
        props.isReadOnly || !isPayerSeeded
            ? formDefaultValues?.payer
            : dataWatcher.payer

    const onRunCoverageCheck = (data: IDynamicCheckerFormInputData) => {
        if (!selectedPracticeRole) {
            return
        }

        const thunkData: IThunkActionWithPracticeData<
            IDynamicCheckerFormInputData[]
        > = {
            payload: [data],
            practice: selectedPracticeRole.practice,

            onSuccess: () => {
                dispatch(
                    checkerSetRunningState(
                        CoverageCheckerRunningState.RESULTS_MANUAL,
                    ),
                )
            },
        }

        switch (effectiveManualCheckType) {
            case QuotaUsageType.GENERAL:
                dispatch(checkerRunGeneralChecks(thunkData))

                break

            case QuotaUsageType.SMART_SCAN:
                dispatch(checkerRunSmartScanChecks(thunkData))

                break

            case QuotaUsageType.DISCOVER:
                dispatch(checkerRunDiscoverChecks(thunkData))

                break

            case QuotaUsageType.MEDICAID:
                dispatch(checkerRunMedicaidChecks(thunkData))

                break

            case QuotaUsageType.AGENTIC_PHONE:
                dispatch(checkerRunAgenticPhoneChecks(thunkData))

                break

            case QuotaUsageType.AGENTIC_PORTAL:
                ToastrHelper.warning(
                    `${manualCheckType} checks are not supported yet.`,
                )

                break
        }

        // Add random part to the url, so we can safely catch browser back button then
        // And if back button is clicked from showing results -> it will initiate "run new checks"
        // Acts as history.push
        navigate(
            `${ROUTES_CONFIG.coverageCheckerUrl}?${
                GENERAL_CONFIG.urlSearchParamsKeys.results
            }=${new Date().getTime()}`,
        )
    }

    return (
        <div
            data-produktly="checker-single-dynamic-form"
            className={`
                relative
                ${props.className}
            `}
        >
            {!props.isReadOnly ? (
                <div className="w-full flex justify-end mb-[16px]">
                    <div
                        className="cursor-pointer"
                        onClick={() => {
                            props.resetForm()
                        }}
                    >
                        <img src={undoPurpleIcon} alt="Clear Form" />
                    </div>
                </div>
            ) : null}

            <form
                data-produktly={`checker-single-required-fields-${
                    ProduktlyHelper.formatId(manualCheckType) || "custom"
                }`}
                onSubmit={handleSubmit(onRunCoverageCheck)}
                noValidate={true}
            >
                <InputElement
                    name="id"
                    type="hidden"
                    reactHookControl={control as any}
                    reactHookValidations={{
                        required: FRONT_VALIDATION_CONFIG.required,
                    }}
                    defaultValue={coverageCheckId}
                />

                <div
                    className="
                        flex mt-[24px]
                        md:block
                    "
                >
                    <InputElement
                        className="
                            mr-[24px] flex-1
                            md:mr-[0px]
                        "
                        label="Member First Name"
                        name="firstName"
                        tooltip={TOOLTIPS_CONFIG.checker.firstName}
                        reactHookControl={control as any}
                        reactHookValidations={{
                            required: FRONT_VALIDATION_CONFIG.required,
                            maxLength: FRONT_VALIDATION_CONFIG.maxLength,
                        }}
                        reactHookErrors={errors}
                        defaultValue={formDefaultValues?.firstName}
                        disabled={props.isReadOnly}
                        isLabelStatic
                    />

                    <InputElement
                        className="
                            flex-1
                            md:mt-[40px]
                        "
                        label="Member Last Name"
                        name="lastName"
                        tooltip={TOOLTIPS_CONFIG.checker.lastName}
                        reactHookControl={control as any}
                        reactHookValidations={{
                            required: FRONT_VALIDATION_CONFIG.required,
                            maxLength: FRONT_VALIDATION_CONFIG.maxLength,
                        }}
                        reactHookErrors={errors}
                        defaultValue={formDefaultValues?.lastName}
                        disabled={props.isReadOnly}
                        isLabelStatic
                    />
                </div>

                <div
                    className="
                        flex mt-[40px]
                        md:block
                    "
                >
                    <DatePickerElement
                        className="
                            mr-[24px] flex-1
                            md:mr-[0px]
                        "
                        name="dob"
                        label="Date of Birth"
                        defaultValue={
                            formDefaultValues?.dob
                                ? moment(formDefaultValues.dob)
                                : undefined
                        }
                        reactHookFormValidations={{
                            required: FRONT_VALIDATION_CONFIG.required,
                        }}
                        shouldValidate
                        reactHookFormErrors={errors}
                        reactHookFormRegister={register as any}
                        reactHookFormUnregister={unregister as any}
                        reactHookFormSet={setValue as any}
                        maxDate={maxDob}
                        disabled={props.isReadOnly}
                        isLabelStatic
                    />

                    <div className="flex-1" />
                </div>

                {initialDataWasSet || props.isReadOnly ? (
                    <>
                        <div
                            className="
                                flex mt-[40px]
                                md:block
                            "
                        >
                            <PayerInput
                                searchOnFocus
                                key={payerInputKey}
                                className="mr-[24px] flex-1 md:mr-[0px]"
                                onPayerSelected={onPayerSelected}
                                isAgenticCheck={isAgenticCheck}
                                disabled={props.isReadOnly}
                                formDefaultValues={{
                                    ...formDefaultValues,

                                    payer:
                                        manualCheckType ===
                                        QuotaUsageType.MEDICAID
                                            ? LookupHelper.getMedicaidDefaultPayer()
                                            : manualCheckType ===
                                                QuotaUsageType.SMART_SCAN
                                              ? undefined
                                              : isMedicaidPayerSelected(
                                                      defaultPayer,
                                                  )
                                                ? undefined
                                                : defaultPayer,
                                }}
                            />

                            {(effectiveManualCheckType &&
                                [
                                    QuotaUsageType.GENERAL,
                                    QuotaUsageType.DISCOVER,
                                    QuotaUsageType.SMART_SCAN,
                                ].includes(effectiveManualCheckType)) ||
                            isAgenticCheck ? (
                                <MemberIdInput
                                    key={memberIdInputKey}
                                    className="flex-1 md:mt-[40px]"
                                    withNotAvailableCheckbox={
                                        !props.isReadOnly && !isAgenticCheck
                                    }
                                    onApplyPayerPrediction={
                                        props.isReadOnly ||
                                        effectiveManualCheckType !==
                                            QuotaUsageType.GENERAL
                                            ? undefined
                                            : applyPredictedPayer
                                    }
                                    required
                                    isLabelStatic
                                    formDefaultValue={
                                        props.isReadOnly
                                            ? formDefaultValues?.memberId
                                            : formDefaultValuesContinuousMonitoring?.memberId ||
                                              formDefaultValues?.memberId
                                    }
                                    disabled={
                                        props.isReadOnly ||
                                        effectiveManualCheckType ===
                                            QuotaUsageType.SMART_SCAN ||
                                        !!dataWatcher.memberIdNotAvailable
                                    }
                                    formControlDisabled={
                                        props.isReadOnly ||
                                        !!dataWatcher.memberIdNotAvailable ||
                                        effectiveManualCheckType ===
                                            QuotaUsageType.SMART_SCAN
                                    }
                                    notAvailableDefaultChecked={
                                        effectiveManualCheckType ===
                                        QuotaUsageType.SMART_SCAN
                                            ? true
                                            : typeof dataWatcher.memberIdNotAvailable !==
                                                "undefined"
                                              ? dataWatcher.memberIdNotAvailable
                                              : !!formDefaultValues?.memberIdNotAvailable
                                    }
                                    notAvailableDisabled={
                                        props.isReadOnly ||
                                        effectiveManualCheckType ===
                                            QuotaUsageType.SMART_SCAN ||
                                        !!dataWatcher.memberId
                                    }
                                />
                            ) : (
                                <div className="flex-1" />
                            )}
                        </div>

                        {effectiveManualCheckType ? (
                            <>
                                <div
                                    className="
                                        flex mt-[36px]
                                        md:block
                                    "
                                >
                                    <InputElement
                                        className="
                                            mr-[24px] flex-1
                                            md:mr-[0px]
                                        "
                                        label={
                                            isAgenticCheck
                                                ? "Billing Provider NPI"
                                                : "NPI"
                                        }
                                        name="providerNpi"
                                        placeholder="1234567890"
                                        reactHookErrors={errors}
                                        reactHookControl={control as any}
                                        reactHookValidations={{
                                            required:
                                                FRONT_VALIDATION_CONFIG.required,
                                            pattern:
                                                FRONT_VALIDATION_CONFIG.npi,
                                        }}
                                        defaultValue={
                                            formDefaultValues?.providerNpi ||
                                            (!isAgenticCheck
                                                ? selectedPracticeRole?.practice
                                                      .groupNPI
                                                : undefined)
                                        }
                                        disabled={props.isReadOnly}
                                        isLabelStatic
                                    />

                                    <NetworkInput
                                        className="
                                            flex-1 mt-[12px]
                                            md:mt-[20px]
                                        "
                                        manualCheckType={
                                            effectiveManualCheckType
                                        }
                                        firstCheckInputInState={
                                            formDefaultValues
                                        }
                                        disabled={props.isReadOnly}
                                    />
                                </div>

                                {isAgenticCheck ? (
                                    <AgenticTaxIdRenderingNameInput
                                        className="mt-[40px]"
                                        formDefaultValues={formDefaultValues}
                                    />
                                ) : null}

                                {effectiveManualCheckType ===
                                QuotaUsageType.MEDICAID ? (
                                    <MedicaidStateMemberIdGenderInput
                                        className="mt-[40px]"
                                        formDefaultValues={
                                            props.isReadOnly
                                                ? formDefaultValues
                                                : formDefaultValuesMedicaid
                                        }
                                        disabled={props.isReadOnly}
                                    />
                                ) : null}

                                {[
                                    QuotaUsageType.DISCOVER,
                                    QuotaUsageType.SMART_SCAN,
                                ].includes(effectiveManualCheckType) ? (
                                    <div
                                        className="
                                            flex mt-[40px]
                                            md:block
                                        "
                                    >
                                        {effectiveManualCheckType ===
                                            QuotaUsageType.DISCOVER &&
                                        !payerSupportsDiscover &&
                                        dataWatcher.payer ? (
                                            <InputErrorElement text="Unfortunately, selected payer does not support Discover" />
                                        ) : (
                                            <>
                                                <InputElement
                                                    className="
                                                        mr-[24px] flex-1
                                                        md:mr-[0px]
                                                    "
                                                    label="ZIP"
                                                    placeholder="10000"
                                                    name="zipCode"
                                                    reactHookControl={
                                                        control as any
                                                    }
                                                    reactHookValidations={{
                                                        required:
                                                            FRONT_VALIDATION_CONFIG.required,
                                                        pattern:
                                                            FRONT_VALIDATION_CONFIG.zipShort,
                                                    }}
                                                    reactHookErrors={errors}
                                                    defaultValue={
                                                        formDefaultValues?.zipCode
                                                    }
                                                    disabled={props.isReadOnly}
                                                    isLabelStatic
                                                />

                                                <SessionRateInput
                                                    className={`
                                                        flex-1 md:mt-[40px]
                                                        ${
                                                            !secondaryDataWasSet
                                                                ? "hidden"
                                                                : ""
                                                        }
                                                    `}
                                                    defaultSessionRate={
                                                        formDefaultValues?.sessionRate
                                                    }
                                                    disabled={props.isReadOnly}
                                                />

                                                {/*PLACEHOLDER for when only one input is shown in row*/}
                                                {!secondaryDataWasSet ? (
                                                    <div className="flex-1" />
                                                ) : null}
                                            </>
                                        )}
                                    </div>
                                ) : null}

                                <div
                                    className={
                                        !secondaryDataWasSet &&
                                        !props.isReadOnly
                                            ? "hidden"
                                            : ""
                                    }
                                >
                                    {effectiveManualCheckType ===
                                        QuotaUsageType.GENERAL ||
                                    isAgenticCheck ? (
                                        <div
                                            className="
                                                flex mt-[40px]
                                                md:block
                                            "
                                        >
                                            <SessionRateInput
                                                className="
                                                    mr-[24px] flex-1
                                                    md:mr-[0px]
                                                "
                                                defaultSessionRate={
                                                    formDefaultValues?.sessionRate
                                                }
                                                disabled={props.isReadOnly}
                                            />

                                            <div className="flex-1" />
                                        </div>
                                    ) : null}

                                    {!props.isReadOnly ? (
                                        <OptionalFieldsInput
                                            className="mt-[40px]"
                                            formDefaultValues={
                                                formDefaultValues
                                            }
                                        />
                                    ) : null}

                                    <div className="flex items-center justify-between mt-[32px]">
                                        <CheckerCreditsIndicatorComponent
                                            inNetworkCheckEnabled={
                                                dataWatcher.inNetworkCheck
                                            }
                                            outNetworkCheckEnabled={
                                                dataWatcher.outNetworkCheck
                                            }
                                        />

                                        <ButtonElement
                                            dataProduktly="checker-run-demo-check-button"
                                            label={
                                                props.isReadOnly
                                                    ? "Run demo check"
                                                    : "Continue"
                                            }
                                            type="primary"
                                            htmlType="submit"
                                        />
                                    </div>
                                </div>
                            </>
                        ) : null}
                    </>
                ) : null}
            </form>
        </div>
    )
}

interface ICheckerDynamicFormComponentProps {
    className?: string
    resetForm: () => void
    // Demo mode: form is prefilled and fields are not editable
    isReadOnly?: boolean
    prefilledFormData?: IDynamicCheckerFormInputData
    // Demo mode: check type from the selected policy (avoids race with redux)
    forcedCheckType?: QuotaUsageType
}
